import React from 'react';
import { Box, ButtonBase, CircularProgress, Typography, styled } from '@mui/material';
import { ReactComponent as BloodSugarIconSvg } from 'assets/icons/BloodSugarIcon.svg';
import { ReactComponent as ChevronRightSvg } from 'assets/icons/ChevronRightIcon.svg';
import { SvgIcon } from 'components/SvgIcon/SvgIcon';
import { WidgetCard } from 'components/WidgetCard/WidgetCard';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { BiometricDatapointRecord } from 'services/biometricsService';
import { toBloodGlucoseWeekSeries } from 'features/progress/utils/bloodGlucoseMeals';
import { useChartReadingWindows } from 'features/progress/hooks/useChartReadingWindows';
import { getYAxisRange } from 'features/progress/utils/bloodGlucoseChartUtils';
import { BloodSugarChart } from './BloodSugarChart';

const CenteredState = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(6),
}));

// Only the arrow opens the detail page. The padding enlarges its tap target, and the negative margin
// cancels it so the header layout is unchanged. The right margin keeps the arrow where WidgetCard's own
// chevron sits: 8px (16px from `sm`) in from the header padding.
const ChevronButton = styled(ButtonBase)(({ theme }) => ({
  padding: theme.spacing(1.5),
  margin: theme.spacing(-1.5, -0.5, -1.5, -1.5),
  borderRadius: '50%',
  fontSize: 16,
  '& svg': { width: '1em', height: '1em' },
  [theme.breakpoints.up('sm')]: { fontSize: 18, marginRight: theme.spacing(0.5) },
}));

export interface BloodSugarWidgetProps {
  onClick: () => void;
  datapoints?: BiometricDatapointRecord[];
  isLoading?: boolean;
  isError?: boolean;
}

export const BloodSugarWidget = ({
  onClick,
  datapoints = [],
  isLoading = false,
  isError = false,
}: BloodSugarWidgetProps) => {
  const { translation } = useTranslationContext();
  // Same window as the detail page's current view: the last 7 days that have readings.
  const { days } = useChartReadingWindows(datapoints);
  const series = toBloodGlucoseWeekSeries(datapoints, days);

  const renderBody = () => {
    if (isLoading) {
      return (
        <CenteredState>
          <CircularProgress aria-label={translation('blood_sugar')} />
        </CenteredState>
      );
    }

    if (isError) {
      return (
        <CenteredState>
          <Typography variant="footnote">{translation('data_unable_to_load')}</Typography>
        </CenteredState>
      );
    }

    return <BloodSugarChart series={series} yRange={getYAxisRange(datapoints)} />;
  };

  return (
    <WidgetCard
      icon={<SvgIcon component={BloodSugarIconSvg} sx={{ color: 'secondary.main' }} />}
      title={translation('blood_sugar')}
      headerAction={
        <ChevronButton aria-label={translation('blood_sugar')} onClick={onClick}>
          <SvgIcon component={ChevronRightSvg} aria-hidden="true" focusable="false" />
        </ChevronButton>
      }
    >
      {renderBody()}
    </WidgetCard>
  );
};
